import { Locale, Markets } from '../../generated/graphql';
import { getLocale } from '../../modules/localesMapper';
import getSearchClient from 'modules/searchClient';
import { PressReleaseAlgolia } from 'components/Press/ListingPage';
import { SearchIcon } from 'components/Icons/icons';
import { GetServerSideProps } from 'next';
import { IGlobalMetaData } from 'types/GlobalMetadata';
import React, { Fragment, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getCopy } from 'lib/utils/getCopy';
import { getPressPageIntro } from 'lib/graphql/getPressPageIntro';
import getConfig from 'next/config';
import { DictionaryItem } from '../../types/Dictionary';
import { algoliaPrefixes, facetsKeys } from '../api/webhook';
import { FilterMenu } from '../../components/FilterMenu';
import { SearchCategorySelector } from '../../components/SearchCategorySelector';
import { SortAndFilter } from '../../components/SortAndFilter';
import { SearchResponse } from '@algolia/client-search';
import { CorporateButton } from '../../components/CorporateButton';
import styled from 'styled-components';
import PressListingPageListItem from '../../components/Press/ListingPageListItem';
import { ShortHero } from '../../components/ShortHero';
import { Text } from '../../components/Typography';
import Custom404 from '../404';
import { Transition } from '@headlessui/react';

const FilterCategoryWrapper = styled.div<{
  children?: React.ReactNode;
  className?: string;
}>`
  @media screen and (min-width: 768px) {
    display: flex;
    align-items: center;
  }
`;
const CategoryWrapper = styled.div<{
  children?: React.ReactNode;
  className?: string;
}>`
  @media screen and (min-width: 768px) {
    width: calc(100% - 310px);
  }
`;

const FilterWrapper = styled.div<{
  children?: React.ReactNode;
  className?: string;
}>`
  @media screen and (min-width: 768px) {
    width: 300px;
  }
`;

const SearchInput = styled.input<{
  type?: string;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}>`
  border-radius: 3rem;
`;
const getFilterString = (typeFilter: string, currLocale: string, currMarket: string) => {
  let localeForFilter = currLocale;
  // show german localization on at and ch
  if (currMarket === Markets.at || currMarket === Markets.ch) {
    localeForFilter = Markets.de;
  }

  let filterString = `(market:${currMarket} OR market:global) AND (market_locale:${currMarket}_default OR market_locale:global_${localeForFilter})`;

  if (typeFilter && typeFilter !== '*') {
    if (typeFilter === 'global') {
      filterString = `(market:${typeFilter}) AND (market_locale:global_${localeForFilter})`;
    } else {
      filterString = `(market:${typeFilter}) AND (market_locale:${currMarket}_default)`;
    }
  }

  return filterString;
};

function PressIndexPage({
  currMarket,
  currLocale,
  pageData,
  serverError,
  dictionary,
  algoliaData,
  algoliaDataDefault,
}: {
  pageData: any;
  globalMetadata: IGlobalMetaData;
  currLocale: Locale;
  currMarket: Markets;
  serverError: boolean;
  dictionary: DictionaryItem[];
  algoliaData: SearchResponse<any>;
  algoliaDataDefault: SearchResponse<any>;
}) {
  const router = useRouter();
  const [pressReleasesData, setPressReleasesData] =
    useState<SearchResponse<PressReleaseAlgolia>>(algoliaData);
  const [defaultPressReleasesData, setDefaultPressReleasesData] =
    useState<SearchResponse<PressReleaseAlgolia>>(algoliaDataDefault);
  const initialFilterState = (() => {
    const yearFacet = algoliaDataDefault?.facets?.year || {};
    const topicFacet = algoliaDataDefault?.facets?.topicEnum || {};
    // get markets
    const years: any = Object.keys(yearFacet).map((year) => ({
      key: year,
      label: year,
      selected: false,
      itemCount: yearFacet[year],
    }));
    // get categories
    const topics: any = Object.keys(topicFacet).map((topic) => ({
      key: topic,
      label: getCopy(`press_tag_${topic}`, dictionary),
      selected: false,
      itemCount: topicFacet[topic],
    }));
    //
    return [
      {
        key: 'year',
        label: getCopy('year', dictionary),
        options: years,
        type: 'text',
      },
      {
        key: 'topics',
        label: getCopy(`topic`, dictionary),
        options: topics,
        type: 'text',
      },
    ];
  })();
  const [query, setQuery] = useState<string>(() => (router.query?.query as string) || '');
  const [hasLoaded, setLoaded] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<any>(initialFilterState);
  const { publicRuntimeConfig } = getConfig();
  const searchClient = getSearchClient(
    publicRuntimeConfig.algoliaAppId,
    publicRuntimeConfig.algoliaApiKey,
  );
  const searchClientDefault = getSearchClient(
    publicRuntimeConfig.algoliaAppId,
    publicRuntimeConfig.algoliaApiKey,
  );
  const index = searchClient.initIndex(algoliaPrefixes['PressReleases']);

  let typeFilterValues: Record<string, string> = {
    '*': getCopy('all', dictionary),
    global: getCopy('press_release_international_', dictionary),
  };
  if (currMarket !== 'global')
    typeFilterValues[`${currMarket}`] = getCopy('press_release_$countryname', dictionary);

  const [typeFilter, setTypeFilter] = useState(currMarket === Markets.it ? currMarket : '*');
  const resultsPerPage: number = 12;
  const [perPageHits, setPerPageHits] = useState<number>(resultsPerPage);

  const searchData = async () => {
    const { topic, year, category, page } = router.query;
    let filterString = getFilterString(typeFilter, currLocale, currMarket);
    let filterStringDefault = getFilterString(typeFilter, 'default', currMarket);
    if (filters) {
      const years = filters
        .filter((filter: any) => filter.key === 'year')
        .pop()
        .options.filter((year: any) => year.selected)
        .map((year: any) => `year:${year.key}`);
      const topics = filters
        .filter((filter: any) => filter.key === 'topics')
        .pop()
        .options.filter((topic: any) => topic.selected)
        .map((topic: any) => `topicEnum:${topic.key}`);

      if (years.length) {
        filterString += ` AND (${years.join(' OR ')})`;
        filterStringDefault += ` AND (${years.join(' OR ')})`;
      }
      if (topics.length) {
        filterString += ` AND (${topics.join(' OR ')})`;
        filterStringDefault += ` AND (${topics.join(' OR ')})`;
      }
    }

    // 1. Fix fallback language
    // As algolia is not able to provide fallback items for localizations as graphcms is,
    // we pull press releases in `default` language and use this as source of truth for filter etc...
    // In addition, we pull the same content in the current language. Later we iterate
    // over the `default` items and see if there is a localized version available.

    // pull content in `default` language
    const defaultResponse = await index.search<PressReleaseAlgolia>(query, {
      hitsPerPage: perPageHits,
      query: query,
      filters: filterStringDefault,
    });
    setDefaultPressReleasesData(defaultResponse);

    // pull localized content
    const response = await index.search<PressReleaseAlgolia>(query, {
      hitsPerPage: perPageHits,
      query: query,
      filters: filterString,
    });
    setPressReleasesData(response);
  };

  useEffect(() => {
    if (!hasLoaded) setLoaded(true);
  }, [hasLoaded]);

  if (pageData && Array.isArray(pageData)) {
    pageData = pageData[0];
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      if (hasLoaded) {
        searchData();
      }
    }, 120);

    return () => {
      clearTimeout(handler);
    };
  }, [query, router.query, typeFilter, filters, perPageHits, hasLoaded]);

  const loadMoreHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    if (perPageHits < defaultPressReleasesData.nbHits) {
      setPerPageHits(perPageHits + resultsPerPage);
    }
  };

  if (serverError) {
    return <Custom404 dictionary={dictionary} />;
  }

  return (
    <>
      {hasLoaded && (
        <div className={`w-full h-full`}>
          <FilterMenu
            id="search-filter-menu"
            isOpen={showFilters}
            totalResults={defaultPressReleasesData.nbHits}
            title={'Filter'}
            onClose={() => setShowFilters(false)}
            onApplyFilters={(updatedFilters: any) => setFilters(updatedFilters)}
            filters={filters}
            labels={{
              showAll: 'Show All',
              showAllResults: 'Show All Results',
              showLess: 'Show Less',
              applyFilters: 'Apply Filters',
            }}
          />
        </div>
      )}
      <ShortHero
        data={{
          superTitle: pageData.title,
          title: pageData.subtitle,
          description: pageData.introText,
        }}
      />
      <div className="w-11/12 max-w-4xl mx-auto ">
        <div className="relative w-full my-5">
          <SearchIcon className="absolute -translate-y-1/2 left-5 top-1/2 h-4" />
          <SearchInput
            type="text"
            className="flex items-center justify-center w-full px-10 py-5 border-1 rounded-2xl border-corporate-blue "
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            placeholder={getCopy('search', dictionary)}
          />
        </div>
        {!defaultPressReleasesData.hits.length && (
          <Text type="lg" tag="p" className="mb-14 text-center font-medium">
            {getCopy('search_no_results_press', dictionary)}
          </Text>
        )}
      </div>
      <FilterCategoryWrapper className="px-small pb-10 2xl:px-medium 3xl:px-xlarge w-full mt-10">
        <CategoryWrapper className="mb-4 md:mb-0 pb-2">
          <SearchCategorySelector
            selected={typeFilter === null ? 'all' : typeFilter}
            categories={typeFilterValues}
            onSelect={(val) => setTypeFilter(val)}
          />
        </CategoryWrapper>
        <FilterWrapper>
          <SortAndFilter
            currentCount={defaultPressReleasesData.nbHits}
            currentFiltersCount={defaultPressReleasesData.hits.length}
            labels={{
              results: getCopy('results', dictionary),
              filters: getCopy('filters', dictionary),
            }}
            showFilter={true}
            onFilter={() => {
              setShowFilters(true);
            }}
          />
        </FilterWrapper>
      </FilterCategoryWrapper>
      {defaultPressReleasesData &&
        defaultPressReleasesData.hits.map((hit: any) => {
          // 2. Fix fallback language
          // Check if we have a localized version of the press release
          let currentHit = hit;
          for (const localizedHit of pressReleasesData.hits) {
            if (currentHit.id === localizedHit.id) {
              currentHit = localizedHit;
              break;
            }
          }
          return (
            <Transition appear={hasLoaded} show={true} as={Fragment} key={currentHit.id}>
              <Transition.Child
                as="div"
                className="overflow-hidden bg-white"
                enter="transform ease-out duration-300 transition"
                enterFrom="translate-y-12 opacity-0 sm:translate-x-0"
                enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                leave="transition ease-in duration-300"
                leaveFrom="opacity-300"
                leaveTo="opacity-0 h-0"
              >
                <PressListingPageListItem copy={dictionary} item={currentHit} />
              </Transition.Child>
            </Transition>
          );
        })}

      {perPageHits < defaultPressReleasesData.nbHits && (
        <div className="flex justify-center mt-5 mb-10">
          <CorporateButton
            buttonType="button"
            type="button"
            color="primaryalternative"
            label={getCopy('load_more_button', dictionary)}
            onClick={loadMoreHandler}
          />
        </div>
      )}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'global',
  locales,
  query,
}) => {
  const error = false;
  const slug = 'press';
  const market = locale; // locale from path is representing market
  const currLocale = getLocale(market);
  const { publicRuntimeConfig } = getConfig();

  const searchClient = getSearchClient(
    publicRuntimeConfig.algoliaAppId,
    publicRuntimeConfig.algoliaApiKey,
  );
  const index = searchClient.initIndex(algoliaPrefixes['PressReleases']);
  const pageData = await getPressPageIntro(currLocale);
  const filterString = getFilterString('*', currLocale, market);
  const indexSearch = await index.search('', {
    facets: facetsKeys['PressReleases'],
    hitsPerPage: 12,
    filters: filterString,
  });

  const filterStringDefault = getFilterString('*', 'default', market);
  const indexSearchDefault = await index.search('', {
    facets: facetsKeys['PressReleases'],
    hitsPerPage: 12,
    filters: filterStringDefault,
  });

  return {
    props: {
      currLocale,
      locales,
      currMarket: market,
      pageData: pageData.data.page,
      metaData: pageData.data.page[0].metaData,
      serverError: error,
      algoliaData: indexSearch,
      algoliaDataDefault: indexSearchDefault,
    },
  };
};
export default PressIndexPage;
